import { useRouter } from "next/router";
import { useEffect } from "react";


export function confirmKeepChanges(warningText: string = 'Are you sure you want to leave this page? Progress will not be saved.') {
    if (window.confirm(warningText)) return true
    return false
}

/**
 * Custom hook that adds a prompt to save progress before leaving the page.
 * 
 * @param {boolean} pristine - Indicates whether the form is in its initial state or has been modified.
 * @param {string} warningText - The text to display in the prompt dialog. Defaults to 'Are you sure you want to leave this page? Progress will not be saved.'
 * 
 * @returns {void}
 */
export function useSaveProgressPrompt(pristine: boolean, warningText: string = 'Are you sure you want to leave this page? Progress will not be saved.') {
    const router = useRouter()
    const handleBrowseAway = (url, rest) => {
        if (url === router.asPath) return
        if (pristine) return

        if (confirmKeepChanges(warningText)) return
        router.events.emit('routeChangeError')
        throw 'routeChange aborted.'
    }

    const handleWindowClose = (e) => {
        if (pristine) return
        e.preventDefault()
        return (e.returnValue = warningText)
    }

    useEffect(() => {
        window.addEventListener('beforeunload', handleWindowClose)
        router.events.on('routeChangeStart', handleBrowseAway)

        return () => {
            window.removeEventListener('beforeunload', handleWindowClose)
            router.events.off('routeChangeStart', handleBrowseAway)
        }
    }, [pristine])

    useEffect(() => {
        // clear warning when calling component unmounts
        return () => {
            window.onbeforeunload = null
            router.events.off('routeChangeStart', handleBrowseAway)
        }
    }, [])
}

export const SaveProgressHoC = ({ pristine, children }: { pristine: boolean, children: React.ReactChild }) => {
    useSaveProgressPrompt(pristine)
    return children
}